import { gsap } from "gsap";
import { IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { GameFont } from "../../../../common/GameFont";
import { ButtonWithScaleAnimation } from "../../../../common/components/ButtonWithScaleAnimation";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { LoginResourcePackage } from "../../resources/LoginResourcePackage";
import { LoginHtmlInput } from "by/gamefactory/cityversetycoon/scenes/common/components/LoginHtmlInput";
import { IAnalyticProviderWrapper } from "../../../../../../analytics/IAnalyticProviderWrapper";
import { TextWithUnderline } from "by/gamefactory/cityversetycoon/scenes/game/components/TextWithUnderline";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { IGuestModel } from "../../../../common/models/IGuestModel";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";
import { IFocusable } from "by/gamefactory/cityversetycoon/scenes/layers/IFocusable";
import { StringFormatExtention } from "../../../../../../extensions/StringFormatExtention";
import { VirtualScreenEvent } from "../../../../../../analytics/events/VirtualScreenEvent";
import { BaseScalableView } from "by/gamefactory/cityversetycoon/scenes/common/BaseScalableView";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";
import { IPasskeyWalletPopupController } from "../controller/IPasskeyWalletPopupController";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";

export class PasskeyAuthPopupView extends BaseScalableView<boolean> implements IFocusable {
    public readonly focusable: boolean = true;

    private static readonly HEADER_TEXT = "CONNECT\nWITH PASSKEY";

    private static readonly DESCRIPTION_TEXT =
        "FOR A SECURE AND SEAMLESS LOGIN EXPERIENCE,\nUSE YOUR PASSKEY.\n\nCREATE A NEW PASSKEY OR LOGIN WITH\nYOUR EXISTING PASSKEY";
    private static readonly CONTINUE_BUTTON_TEXT = "CONTINUE";
    private static readonly CREATE_BUTTON_TEXT = "CREATE";
    private static readonly CONNECT_BUTTON_TEXT = "CONNECT";
    private static readonly FOOTER_TEXT = "WE'LL SET UP AN IN-GAME WALLET FOR YOU USING PASSKEY";

    private readonly loginResources: LoginResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly passkeyAuthController: IPasskeyWalletPopupController;
    private readonly analyticsProvider: IAnalyticProviderWrapper;
    private readonly guestModel: IGuestModel;
    private readonly asyncOperationNotification: AsyncOperationNotification;
    private readonly logger: ILogger;

    private fadeScreen: FadeScreen;
    private backButton: ButtonWithScaleAnimation;
    private emailIcon: Sprite;
    private headerText: Text;
    private descriptionText: Text;
    private continueButton: ButtonWithScaleAnimation;
    private createButton: ButtonWithScaleAnimation;

    private resetButtonText: Text;
    private createButtonText: Text;
    private loginButtonText: Text;
    private emailInput: LoginHtmlInput;
    private footerText: Text;
    private hasStoredPasskey: boolean;

    public constructor(
        operationControls: PromiseCompletionSource<boolean>,
        loginResources: LoginResourcePackage,
        commonResources: CommonResourcesPackage,
        canvasScaler: ICanvasScaler,
        passkeyAuthController: IPasskeyWalletPopupController,
        analyticsProvider: IAnalyticProviderWrapper,
        guestModel: IGuestModel,
        asyncOperationNotification: AsyncOperationNotification,
        logger: ILogger
    ) {
        super(operationControls, canvasScaler);
        this.loginResources = loginResources;
        this.commonResources = commonResources;
        this.passkeyAuthController = passkeyAuthController;
        this.analyticsProvider = analyticsProvider;
        this.guestModel = guestModel;
        this.asyncOperationNotification = asyncOperationNotification;
        this.logger = logger;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public start(): void {
        this.show();
    }

    private build(): void {
        this.fadeScreen = new FadeScreen(0.1, 0, 0.8, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fadeScreen.eventMode = "static";
        this.addChild(this.fadeScreen);

        this.backButton = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height, this.backButton.width * 2, this.backButton.height * 2);
        this.backButton.onUp.add(this.backButtonClickHandle, this);
        this.addChild(this.backButton);

        this.headerText = new Text(PasskeyAuthPopupView.HEADER_TEXT, new HeaderTextStyle());
        this.addChild(this.headerText);

        this.descriptionText = new Text(PasskeyAuthPopupView.DESCRIPTION_TEXT, new DescriptionTextStyle());
        this.addChild(this.descriptionText);

        // Continue | Login Button
        this.continueButton = new ButtonWithScaleAnimation(this.loginResources.assets_views_login_buttons_button_blue_big_png);
        this.continueButton.onUp.add(this.handleLoginButtonClick, this);

        this.resetButtonText = new Text(PasskeyAuthPopupView.CONTINUE_BUTTON_TEXT, new ButtonTextStyle());
        this.loginButtonText = new Text(PasskeyAuthPopupView.CONNECT_BUTTON_TEXT, new ButtonTextStyle());
        this.continueButton.defaultView.addChild(this.loginButtonText);
        this.addChild(this.continueButton);

        // CREATE button
        this.createButton = new ButtonWithScaleAnimation(this.loginResources.assets_views_login_buttons_button_green_blue_big_png);
        this.createButton.onUp.add(this.handleCreateButtonClick, this);

        this.createButtonText = new Text(PasskeyAuthPopupView.CREATE_BUTTON_TEXT, new ButtonTextStyle());
        this.createButton.defaultView.addChild(this.createButtonText);
        this.addChild(this.createButton);

        this.passkeyAuthController.hasStorePasskey().then(
            (result: boolean) => {
                if (result) {
                    this.hasStoredPasskey = true;
                }
            },
            (reason: unknown) => {
                this.logger.debug(reason instanceof Error ? reason.message : String(reason));
            }
        );

        this.footerText = new Text(PasskeyAuthPopupView.FOOTER_TEXT, new FooterTextStyle());
        this.addChild(this.footerText);
    }

    private async show(): Promise<void> {
        if (this.guestModel.email.value != undefined && this.guestModel.email.value != "") {
            this.emailInput.value = StringFormatExtention.emailFormatWithStars(this.guestModel.email.value);
        }
        this.fadeScreen.fadeIn();
        this.emailInput?.hide();
        const showAnimation = new gsap.core.Timeline();
        showAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.15, ease: "power1.in" });
        await showAnimation.play();
        showAnimation.kill();
        this.emailInput?.show();
    }

    private async backButtonClickHandle(): Promise<void> {
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        this.emailInput?.hide();
        await this.hideView();
        this.operationControls.complete(false);
    }

    private async contactUsClickHandle(): Promise<void> {
        this.passkeyAuthController.openIntercom();
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Passkey contact us button click", this.analyticsProvider.currentEventIndex));
    }

    private async handleLoginButtonClick(): Promise<void> {
        await this.continueButtonClickHandle(false);
    }

    private async handleCreateButtonClick(): Promise<void> {
        await this.continueButtonClickHandle(true);
    }

    private async continueButtonClickHandle(isNewPasskeyFlow: boolean): Promise<void> {
        const eventName = isNewPasskeyFlow ? "Passkey create button click " : "Passkey connect button click";
        this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent(eventName, this.analyticsProvider.currentEventIndex));

        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        this.addChild(inputBlocker);
        try {
            const dateString = Date.now().toString(36);
            const randomness = Math.random().toString(36).substr(2);
            const fbEventId = dateString + randomness;
            if (await this.asyncOperationNotification.autoShowHide(this.passkeyAuthController.signWithCode(this.hasStoredPasskey || isNewPasskeyFlow))) {
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Passkey sign in completed", this.analyticsProvider.currentEventIndex));
                await this.hideView();
                this.operationControls.complete(true);
            } else {
                this.analyticsProvider.registerGoogleEvent(new VirtualScreenEvent("Passkey sign in failed", this.analyticsProvider.currentEventIndex));
                await this.hideView();
                await this.passkeyAuthController.showValidationFailedPopup();
            }

            await this.show();
            this.removeChild(inputBlocker);
        } catch (error: unknown) {
            this.logger.debug(error instanceof Error ? error.message : String(error));
            this.analyticsProvider.registerGoogleEvent(
                new VirtualScreenEvent("Email sign in with code verification failed", this.analyticsProvider.currentEventIndex)
            );
            await this.hideView();
            await this.passkeyAuthController.showValidationFailedPopup();
        } finally {
            this.removeChild(inputBlocker);
        }
    }

    private async hideView(): Promise<void> {
        this.emailInput?.hide();
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.15, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
    }

    private layout(width: number, height: number): void {
        this.fadeScreen.width = width;
        this.fadeScreen.height = height;
        this.backButton.position.set(56, 235);
        this.headerText.position.set(80, 415);
        this.headerText.angle = -5;
        this.headerText.skew.x = -0.08;
        this.descriptionText.position.set(67, 745);
        this.continueButton.position.set(213, 1176 - this.continueButton.height * 0.5);
        this.resetButtonText.position.set(
            (this.continueButton.width - this.resetButtonText.width) * 0.5,
            (this.continueButton.height - this.resetButtonText.height) * 0.5
        );
        this.loginButtonText.position.set(
            (this.continueButton.width - this.loginButtonText.width) * 0.5,
            (this.continueButton.height - this.loginButtonText.height) * 0.5
        );

        this.createButton.position.set(415 - this.createButton.width * 0.5, 1338 - this.createButton.height * 0.5);
        this.createButtonText.position.set(
            (this.createButton.width - this.createButtonText.width) * 0.5,
            (this.createButton.height - this.createButtonText.height) * 0.5
        );
        this.footerText.position.set(95, height - this.footerText.height - 92);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.backButton.onUp.remove(this.backButtonClickHandle, this);
        this.continueButton.onUp.remove(this.handleLoginButtonClick, this);
        this.createButton.onUp.remove(this.handleCreateButtonClick, this);
        super.destroy(options);
    }
}

class HeaderTextStyle extends TextStyle {
    public constructor() {
        super(
            new TextStyle({
                align: "center",
                fill: "#ffffff",
                fontFamily: GameFont.DEFAULT_FONT,
                fontSize: 154,
                lineHeight: 123,
                wordWrap: true,
                wordWrapWidth: 700,
            })
        );
    }
}

class DescriptionTextStyle extends TextStyle {
    public constructor() {
        super(
            new TextStyle({
                align: "center",
                fill: "#ffffff",
                fontFamily: GameFont.DEFAULT_FONT,
                fontSize: 44,
                lineHeight: 52,
                letterSpacing: 1.6,
                fontWeight: "400",
            })
        );
    }
}

class ButtonTextStyle extends TextStyle {
    public constructor() {
        super(
            new TextStyle({
                dropShadow: true,
                dropShadowAngle: 0.8,
                dropShadowBlur: 5,
                dropShadowDistance: 2,
                dropShadowAlpha: 0.5,
                fill: "#ffffff",
                align: "center",
                fontFamily: GameFont.DEFAULT_FONT,
                fontSize: 42,
                miterLimit: 0,
                lineHeight: 60,
            })
        );
    }
}

class FooterTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#ADADAD",
            align: "center",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 36,
            lineHeight: 42,
        });
    }
}
